import { SweetBombSoundType } from './../../soundManager/enum/soundType'
import { Assets, BitmapText, Container, Text, TextStyle } from 'pixi.js'
import gsap from 'gsap'
import data from '/resource/spine/spine_sceneSwitch/spine_sceneSwitch_v4.skel?url'
import atlas from '/resource/spine/spine_sceneSwitch/spine_sceneSwitch_v4.atlas?url'
import freeSpinRetriggerSkel from '/resource/spine/freeSpin_retrigger/spine_retrigger_v2.skel?url'
import freeSpinRetriggerAtlas from '/resource/spine/freeSpin_retrigger/spine_retrigger_v2.atlas?url'

import { Spine } from '@esotericsoftware/spine-pixi'
import { gameConfig } from '../../../config'
import { soundManager, SoundType, t, useVoucherStore } from '@slot-nx/core'
import { sleep } from '../../game/util/gameUtil'
import { useGameStore } from '../../game/store/GameStore'
import { numberAnimation } from '../../../components/util/numberAnimationUtil'
import { getGameResult, getGameResultFinalPayout, MachineActionType, sweetBombMachine } from '../../../../machine/sweetbombMachine'
import { FreeGameType } from '../../buybonus/enum/buyBonusEnum'
import { backgroundService } from '../../background'
import { useGodziDropStore } from '../../godziDrop/store/godziDropStore'

const textObject = new Text()

textObject.alpha = 0.2
textObject.style = new TextStyle({
  fontSize: 48,
  fill: '#ffffff',
  align: 'center',
})
textObject.anchor.set(0.5)

export const getFreeGameSpineData = () => {
  const FreeGameSpineData = [
    { alias: 'freeSpinRetriggerSkel', src: freeSpinRetriggerSkel },
    { alias: 'freeSpinRetriggerAtlas', src: freeSpinRetriggerAtlas },
    { alias: 'data', src: data },
    { alias: 'atlas', src: atlas },
  ]

  return FreeGameSpineData
}

class FreeGameService {
  public houseContainer: Container
  public sceneContainer: Container

  public async showEnterFgScene() {
    const gameResult = getGameResult()
    useGameStore.setState({ freeGameType: gameResult.mainGame.freeGameType })
    const closeSceneCb = await this.enterFgScene()

    this.sceneContainer.onpointertap = () => {
      closeSceneCb()
      soundManager.playBGM()
      this.sceneContainer.onpointertap = null
    }
  }

  public async increaseFreeGameCount() {
    const spineContainer = this.sceneContainer.getChildByName('spineContainer') as Container
    const spine = Spine.from(freeSpinRetriggerSkel, freeSpinRetriggerAtlas)
    spineContainer.addChild(spine)

    const triggerAni = `spine_retrigger_in`
    spine.x = gameConfig.resolution.width / 2
    spine.y = gameConfig.resolution.height / 2.4
    const track = spine.state.setAnimation(0, triggerAni)
    soundManager.play(SweetBombSoundType.AddFgSpins)
    soundManager.stopBGM()
    await sleep((track.animation?.duration as number) * 1000)
    soundManager.playBGM()
    spineContainer.removeChild(spine)
  }

  public async showExitFreeGame() {
    const isVoucherTime = useGameStore.getState().isVoucherTime
    const hasRemainVoucher = useVoucherStore.getState().remainSpin > 0
    const finalPayout = getGameResultFinalPayout()
    soundManager.switchBGM(SoundType.bgm)
    await sleep(800)
    soundManager.play(SweetBombSoundType.FgTotalWin)
    const closeSceneCb = await this.showTotalWinScene(this.sceneContainer, finalPayout)

    this.sceneContainer.onpointertap = () => {
      closeSceneCb()
      this.sceneContainer.onpointertap = null
      sweetBombMachine.send({ type: MachineActionType.freeGameSpinEnd })
      backgroundService.changeToFreeGame(false)
      if (!useGodziDropStore.getState().isGodziTime) {
        soundManager.playBGM()
      }
      if (isVoucherTime && !hasRemainVoucher) {
        sweetBombMachine.send({ type: MachineActionType.voucherTimeEnd })
      }
      else {
        sweetBombMachine.send({ type: MachineActionType.freeGameSpinEnd })
      }
    }
  }

  private async enterFgScene() {
    const spineContainer = this.sceneContainer.getChildByName('spineContainer') as Container
    const tabContainer = this.sceneContainer.getChildByName('tabAnywhere') as Container

    const isYummy = useGameStore.getState().freeGameType === FreeGameType.YummyFreeGame
    const spine = Spine.from(data, atlas, { autoUpdate: true })

    spineContainer.addChild(spine)
    const openAni = isYummy ? `free_in_open` : `free_in_open2`
    const loopAni = isYummy ? `free_in_loop` : `free_in_loop2`
    const closeAni = isYummy ? `free_in_close` : `free_in_close2`

    spine.x = gameConfig.resolution.width / 2
    spine.y = gameConfig.resolution.height / 2

    const track = spine.state.setAnimation(0, openAni)
    if (isYummy) {
      soundManager.play(SweetBombSoundType.Fg1Cutscene)
      soundManager.switchBGM(SweetBombSoundType.YummyFgBgm)
    }
    else {
      soundManager.play(SweetBombSoundType.Fg2Cutscene)
      soundManager.switchBGM(SweetBombSoundType.HeavenlyFgBgm)
    }
    backgroundService.changeToFreeGame(true)
    await sleep((track.animation?.duration as number) * 1000)

    // play loop
    spine.state.setAnimation(0, loopAni, true)
    textObject.text = t('freeGame_tap_anywhere')
    tabContainer.addChild(textObject)
    this.tapAnywhereAnimation(textObject)

    // closeScene
    const closeSceneCallback = () => {
      sweetBombMachine.send({ type: MachineActionType.freeGameSpinStart })
      tabContainer.removeChild(textObject)
      const closeTrack = spine.state.setAnimation(0, closeAni, false)
      setTimeout(() => {
        spineContainer.removeChild(spine)
        spine.destroy()
      }, (closeTrack.animation?.duration as number) * 1000)
    }

    return closeSceneCallback
  }

  public tapAnywhereAnimation(text: Text | BitmapText) {
    const tl = gsap.timeline({
      repeat: Infinity,
      yoyo: true,
    })

    tl.fromTo(
      text,
      { alpha: 0.2 },
      {
        duration: 1,
        alpha: 1,
        ease: 'sine.inOut',
      },
    )
  }

  private async showTotalWinScene(sceneContainer: Container, winAmount: number): Promise<() => void> {
    const spineContainer = sceneContainer.getChildByName('spineContainer') as Container
    const tabContainer = sceneContainer.getChildByName('tabAnywhere') as Container
    const totalContainer = sceneContainer.getChildByName('totalWin') as Container

    const isYummy = useGameStore.getState().freeGameType === FreeGameType.YummyFreeGame
    await Assets.load([data, atlas])
    const spine = Spine.from(data, atlas, { autoUpdate: true })
    spineContainer.addChild(spine)
    const openAni = isYummy ? `free_out_open` : `free_out_open2`
    const loopAni = isYummy ? `free_out_loop` : `free_out_loop2`
    const closeAni = isYummy ? `free_out_close` : `free_out_close2`

    spine.x = gameConfig.resolution.width / 2
    spine.y = gameConfig.resolution.height / 2

    const track = spine.state.setAnimation(0, openAni)
    await sleep((track.animation?.duration as number) * 1000)
    spine.state.setAnimation(0, loopAni, true)
    const totalWinText = new BitmapText('0', { fontName: 'FontS', fontSize: 60 })

    totalWinText.anchor.set(0.5, 0.5)
    totalContainer.addChild(totalWinText)

    const [numTask, skipTask] = numberAnimation(0, winAmount, 2, totalWinText)
    const skipNumAnimation = () => {
      skipTask()
      spineContainer.onpointertap = null
    }
    spineContainer.onpointertap = skipNumAnimation
    await numTask

    spineContainer.onpointertap = null
    textObject.text = t('freeGame_tap_anywhere')
    tabContainer.addChild(textObject)
    this.tapAnywhereAnimation(textObject)

    const closeSceneCallback = () => {
      totalContainer.removeChild(totalWinText)
      tabContainer.removeChild(textObject)
      const closeTrack = spine.state.setAnimation(0, closeAni, false)

      setTimeout(() => {
        spineContainer.removeChild(spine)
        spine.destroy()
      }, (closeTrack.animation?.duration as number) * 1000)
    }
    return closeSceneCallback
  }
}

export const freeGameService = new FreeGameService()
